/** A post's dive: its summary ("9.3 m · 56 min · 10 °C") and its depth
 * chart, drawn as SVG by hand (the site has no chart library, and this is one
 * simple shape). Small in post cards; large on the post's page, with water
 * temperature along it, gas switches marked, and the depth, temperature and
 * time at the pointer.
 *
 * Dives come from the API metric (services/dive_profiles.py). Each is drawn
 * in the reader's units and the other's, with a m / ft switch between them:
 * the switch is for that dive only, until they leave the page, and never
 * changes their setting. The app's DiveProfileChart does the same.
 */
import { esc } from "./utils.js";
import {
  IMPERIAL, METRIC, otherUnits, unitLabel, depthIn, temperatureIn,
  formatDepth, formatTemperature, formatPressure,
} from "./units.js";

const SEP = " · ";

/** "56 min": dives are counted in minutes. */
export function formatDiveDuration(seconds) {
  return seconds == null ? "" : `${Math.round(seconds / 60)} min`;
}

/** "Air", "EANx 32", "Trimix 18/45". */
export function gasLabel(gas) {
  if (!gas) return "";
  if (gas.he > 0) return `Trimix ${gas.o2}/${gas.he}`;
  return gas.o2 === 21 ? "Air" : `EANx ${gas.o2}`;
}

/** The dive's start, as the diver's computer showed it. The API sends it
 * without an offset, which JavaScript reads as local time: the same digits
 * wherever it's read, as it should be. */
export function formatDiveStart(startedAt) {
  if (!startedAt) return "";
  return new Date(startedAt).toLocaleString(undefined, {
    weekday: "short", year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}

/** "9.3 m · 56 min · 10 °C": what a card shows, temperature when known. */
export function diveSummaryText(dive, units) {
  return [formatDepth(dive.max_depth_m, units), formatDiveDuration(dive.duration_s),
    formatTemperature(dive.water_temp_c, units)].filter(Boolean).join(SEP);
}

/** The rest, for the post's page: when, average depth, gas, tank, computer. */
export function diveDetails(dive, units) {
  const details = [];
  if (dive.started_at) details.push(["When", formatDiveStart(dive.started_at)]);
  if (dive.avg_depth_m != null) details.push(["Average depth", formatDepth(dive.avg_depth_m, units)]);
  if (dive.gases?.length) details.push([dive.gases.length > 1 ? "Gases" : "Gas", dive.gases.map(gasLabel).join(", ")]);
  if (dive.tank_start_bar != null || dive.tank_end_bar != null) {
    details.push(["Tank", [formatPressure(dive.tank_start_bar, units) || "?", formatPressure(dive.tank_end_bar, units) || "?"].join(" → ")]);
  }
  if (dive.air_temp_c != null) details.push(["Air", formatTemperature(dive.air_temp_c, units)]);
  if (dive.dive_computer) details.push(["Dive computer", dive.dive_computer]);
  return details;
}

/** A "nice" step (1, 2 or 5 × 10ⁿ) giving about `count` ticks up to max. */
function niceStep(max, count) {
  const raw = max / count;
  const power = 10 ** Math.floor(Math.log10(raw));
  return [1, 2, 5, 10].map((m) => m * power).find((s) => s >= raw) ?? raw;
}

// Each panel's height; the SVG's height follows from them. Small charts (in
// cards) draw depth only; large ones add water temperature in a strip below.
const SIZES = {
  small: { width: 600, left: 40, right: 8, top: 6, bottom: 18, depthH: 86 },
  large: { width: 720, left: 48, right: 12, top: 10, bottom: 26, depthH: 200, gap: 30, tempH: 70 },
};

/** The chart's geometry for a dive in some units: its panels, scales and
 * points. Null for a dive without samples. */
export function chartGeometry(dive, units, size = "small") {
  const spec = SIZES[size];
  const times = dive.samples?.time_s ?? [];
  if (times.length < 2) return null;
  const depths = (dive.samples?.depth_m ?? []).map((d) => depthIn(d, units));
  const temps = size === "large"
    ? temperatureAtEachSample((dive.samples?.temp_c ?? []).map((c) => (c == null ? null : temperatureIn(c, units))))
    : [];
  const known = temps.filter((c) => c != null);

  const t0 = times[0];
  const span = Math.max(times[times.length - 1] - t0, 1);
  const deepest = Math.max(...depths, 0.1);
  const depthStep = niceStep(deepest, size === "large" ? 4 : 2);
  // A little below the deepest point, not on to the next tick: a 31 ft dive
  // with an axis to 40 ft leaves a quarter of the chart empty.
  const depthMax = deepest * 1.08;
  const plotW = spec.width - spec.left - spec.right;
  const depthTop = spec.top;
  const depthBottom = depthTop + spec.depthH;
  const x = (t) => spec.left + ((t - t0) / span) * plotW;
  const y = (d) => depthTop + (d / depthMax) * spec.depthH;

  let temp = null;
  if (known.length) {
    // A degree either side, at least, so a dive at one temperature is a line
    // in the middle of its strip rather than along its edge.
    const tempStep = niceStep(Math.max(Math.max(...known) - Math.min(...known), 2), 2);
    const low = Math.floor((Math.min(...known) - 0.5) / tempStep) * tempStep;
    const high = Math.ceil((Math.max(...known) + 0.5) / tempStep) * tempStep;
    const top = depthBottom + spec.gap;
    const bottom = top + spec.tempH;
    temp = { values: temps, low, high, step: tempStep, top, bottom, y: (c) => top + ((high - c) / (high - low)) * spec.tempH };
  }
  const plotBottom = temp ? temp.bottom : depthBottom;
  const box = { ...spec, height: plotBottom + spec.bottom };
  return { box, times, depths, t0, span, depthStep, depthMax, plotW, depthTop, depthBottom, plotBottom, temp, x, y };
}

// About how wide one character of an axis label is (10px text), in the
// chart's own units, for keeping labels inside it.
const AXIS_CHAR_W = 6;

function f(n) {
  return Math.round(n * 10) / 10;
}

function rounded(value) {
  return String(Math.round(value * 10) / 10);
}

/** The chart, as an SVG string, in the given units. Null for a dive without
 * samples (one typed in). */
export function diveChartSvg(dive, units, size = "small") {
  const g = chartGeometry(dive, units, size);
  if (!g) return null;
  const { box, times, depths, x, y, temp } = g;
  const large = size === "large";
  const right = box.width - box.right;
  const hline = (yy) => `<line x1="${box.left}" x2="${right}" y1="${f(yy)}" y2="${f(yy)}" class="dive-grid"/>`;
  // Each panel's sides: depth and temperature are two charts, with two scales.
  const sides = (top, bottom) => [box.left, right].map((xx) =>
    `<line x1="${xx}" x2="${xx}" y1="${f(top)}" y2="${f(bottom)}" class="dive-frame"/>`).join("");
  const tick = (yy, text) => `<text x="${box.left - 5}" y="${f(yy) + 3}" text-anchor="end" class="dive-axis">${esc(text)}</text>`;
  const parts = [];

  // Depth, downwards; the deepest tick names the unit.
  for (let d = 0; d <= g.depthMax + 1e-9; d += g.depthStep) {
    const last = d + g.depthStep > g.depthMax + 1e-9;
    parts.push(hline(y(d)), tick(y(d), last ? `${rounded(d)} ${unitLabel("depth", units)}` : rounded(d)));
  }
  const line = times.map((t, i) => `${i ? "L" : "M"}${f(x(t))},${f(y(depths[i]))}`).join("");
  const area = `${line}L${f(x(times[times.length - 1]))},${f(y(0))}L${f(x(times[0]))},${f(y(0))}Z`;
  parts.push(`<path d="${area}" class="dive-area"/><path d="${line}" class="dive-line"/>`);
  parts.push(sides(g.depthTop, g.depthBottom));

  if (large) {
    for (const event of dive.events ?? []) {
      // A switch at the first sample is the gas the dive started on, named
      // in the details: only changes are marked.
      if (event.type !== "gas_switch" || event.time_s <= times[0]) continue;
      const ex = f(x(event.time_s));
      parts.push(`<line x1="${ex}" x2="${ex}" y1="${g.depthTop}" y2="${f(g.depthBottom)}" class="dive-event"/>`);
      parts.push(`<text x="${ex + 3}" y="${g.depthTop + 10}" class="dive-axis dive-event-label">${esc(gasLabel(event))}</text>`);
    }
  }

  // Water temperature, in its own strip below; the top tick names the unit.
  if (temp) {
    for (let c = temp.high; c >= temp.low - 1e-9; c -= temp.step) {
      const first = c === temp.high;
      parts.push(hline(temp.y(c)), tick(temp.y(c), first ? `${rounded(c)} ${unitLabel("temperature", units)}` : rounded(c)));
    }
    const points = temp.values.map((c, i) => [times[i], c]).filter(([, c]) => c != null);
    const path = points.map(([t, c], i) => `${i ? "L" : "M"}${f(x(t))},${f(temp.y(c))}`).join("");
    parts.push(`<path d="${path}" class="dive-temp"/>`);
    parts.push(sides(temp.top, temp.bottom));
  }

  // Minutes along the bottom, shared by both; the last tick names the unit,
  // as the depth and temperature scales do. A tick whose label would run
  // past the right edge is left out.
  const minutes = g.span / 60;
  const timeStep = niceStep(minutes, large ? 6 : 4);
  const axisY = g.plotBottom + 14;
  const ticks = [];
  for (let m = timeStep; m < minutes; m += timeStep) ticks.push(Math.round(m));
  const fits = (m) => x(g.t0 + m * 60) + (`${m} min`.length * AXIS_CHAR_W) / 2 <= box.width;
  while (ticks.length && !fits(ticks[ticks.length - 1])) ticks.pop();
  ticks.forEach((m, i) => {
    const text = i === ticks.length - 1 ? `${m} min` : String(m);
    parts.push(`<text x="${f(x(g.t0 + m * 60))}" y="${axisY}" text-anchor="middle" class="dive-axis">${text}</text>`);
  });

  if (large) {
    parts.push(`<line class="dive-cursor" x1="0" x2="0" y1="${g.depthTop}" y2="${f(g.plotBottom)}" visibility="hidden"/>`);
  }

  const label = `Depth profile: ${formatDepth(dive.max_depth_m, units)} deep, ${formatDiveDuration(dive.duration_s)}`
    + (temp ? `, with water temperature below` : "");
  return `<svg class="dive-chart dive-chart-${size}" viewBox="0 0 ${box.width} ${box.height}" role="img"
       aria-label="${esc(label)}">
    ${parts.join("")}
  </svg>`;
}

function variantHtml(dive, units, size, shown) {
  const chart = diveChartSvg(dive, units, size);
  const details = size === "large" ? diveDetails(dive, units) : [];
  return `<div class="dive-variant" data-units="${units}"${shown ? "" : " hidden"}>
    <div class="fw-semibold dive-summary">${esc(diveSummaryText(dive, units))}</div>
    ${chart ? `<div class="dive-chart-wrap position-relative">${chart}
      ${size === "large" ? `<div class="dive-tooltip small" hidden></div>` : ""}</div>` : ""}
    ${details.length ? `<dl class="dive-details small mb-0">${details.map(([k, v]) =>
      `<div><dt>${esc(k)}</dt><dd>${esc(v)}</dd></div>`).join("")}</dl>` : ""}
  </div>`;
}

/** The dive, ready to place in a card (size "small") or a post's page
 * ("large"): drawn in both units, the reader's shown, with the m / ft switch.
 * Wire it up with attachDiveProfiles. */
export function diveProfileHtml(dive, { units = METRIC, size = "small" } = {}) {
  const other = otherUnits(units);
  const button = (u) => `<button type="button" class="btn btn-outline-secondary${u === units ? " active" : ""}"
      data-dive-units="${u}" aria-pressed="${u === units}">${unitLabel("depth", u)}</button>`;
  return `<div class="dive-profile dive-profile-${size} border rounded p-2 mb-2">
    <div class="d-flex align-items-start justify-content-between gap-2">
      <div class="text-slate small"><i class="bi bi-water me-1"></i>Dive</div>
      <div class="btn-group btn-group-sm dive-units-switch" role="group" aria-label="Units for this dive">
        ${button(METRIC)}${button(IMPERIAL)}
      </div>
    </div>
    ${variantHtml(dive, units, size, true)}${variantHtml(dive, other, size, false)}
  </div>`;
}

/** Wires the m / ft switch of each dive in container. */
export function attachDiveProfiles(container) {
  container.querySelectorAll(".dive-profile").forEach((profile) => {
    profile.querySelectorAll("[data-dive-units]").forEach((button) => {
      button.addEventListener("click", (e) => {
        e.stopPropagation(); // in a card, the switch doesn't open the post
        const units = button.dataset.diveUnits;
        profile.querySelectorAll(".dive-variant").forEach((v) => { v.hidden = v.dataset.units !== units; });
        profile.querySelectorAll("[data-dive-units]").forEach((b) => {
          const on = b.dataset.diveUnits === units;
          b.classList.toggle("active", on);
          b.setAttribute("aria-pressed", String(on));
        });
      });
    });
  });
}

/** The depth, temperature and time at the pointer (or finger), on a large
 * dive's chart: profile is the element diveProfileHtml made for dive. */
export function attachDivePointer(profile, dive) {
  profile.querySelectorAll(".dive-variant").forEach((v) => attachPointer(v, dive, v.dataset.units));
}

function attachPointer(variant, dive, units) {
  const svg = variant.querySelector(".dive-chart-large");
  const tooltip = variant.querySelector(".dive-tooltip");
  const g = chartGeometry(dive, units, "large");
  if (!svg || !tooltip || !g) return;
  const cursor = svg.querySelector(".dive-cursor");
  const hide = () => { tooltip.hidden = true; cursor.setAttribute("visibility", "hidden"); };

  function show(clientX) {
    const rect = svg.getBoundingClientRect();
    if (!rect.width) return;
    const sx = ((clientX - rect.left) / rect.width) * g.box.width;
    const t = g.t0 + ((sx - g.box.left) / g.plotW) * g.span;
    const i = nearestIndex(g.times, t);
    const at = readingAt(dive, i);
    const parts = [formatDiveDuration(g.times[i] - g.t0), formatDepth(at.depth, units)];
    if (at.temp != null) parts.push(formatTemperature(at.temp, units));
    if (at.pressure != null) parts.push(formatPressure(at.pressure, units));
    tooltip.textContent = parts.join(SEP);
    tooltip.hidden = false;
    const px = g.x(g.times[i]);
    cursor.setAttribute("x1", px);
    cursor.setAttribute("x2", px);
    cursor.setAttribute("visibility", "visible");
    tooltip.style.left = `${(px / g.box.width) * 100}%`;
  }
  svg.addEventListener("pointermove", (e) => show(e.clientX));
  svg.addEventListener("pointerdown", (e) => show(e.clientX));
  svg.addEventListener("pointerleave", hide);
}

/** A temperature for every sample: computers log one only when it changes,
 * so a sample without one has the last one logged (none before the first).
 * Drawn point by point, the line steps where the temperature changed rather
 * than sloping between changes. */
export function temperatureAtEachSample(temps) {
  let last = null;
  return temps.map((c) => {
    if (c != null) last = c;
    return last;
  });
}

/** Index of the sample nearest time t (times ascending). */
export function nearestIndex(times, t) {
  let lo = 0;
  let hi = times.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (times[mid] < t) lo = mid; else hi = mid;
  }
  return Math.abs(times[lo] - t) <= Math.abs(times[hi] - t) ? lo : hi;
}

/** Depth, and the latest temperature and pressure logged by sample i:
 * computers log those only when they change. */
export function readingAt(dive, i) {
  const s = dive.samples;
  const latest = (column) => {
    for (let j = i; j >= 0; j -= 1) if (column?.[j] != null) return column[j];
    return null;
  };
  return { depth: s.depth_m[i], temp: latest(s.temp_c), pressure: latest(s.pressure_bar) };
}
